import { Link } from '@inertiajs/react'
import Seo from '../components/Seo'
import PackCard from '../components/PackCard'
import ThreeDHeadline from '../components/ThreeDHeadline'
import { useShared } from '../lib/hooks'
import type { Pack } from '../lib/types'

export default function Home({ packs }: { packs: Pack[] }) {
  const { site, promo } = useShared()
  const base = site.url
  const freePack = packs.find((p) => p.free)

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Forma Sound',
    url: base,
    description:
      'Commercial preset packs for Serum 2 and other software synthesizers.',
    sameAs: ['https://www.instagram.com/formasound'],
  }
  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Forma Sound',
    url: base,
  }
  const itemListLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: packs.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${base}${p.url}`,
      name: p.name,
    })),
  }

  return (
    <>
      <Seo
        title="Forma Sound"
        description="Commercial preset packs for Serum 2 and other software synthesizers. Built by sound designers, for music producers."
        jsonLd={[orgLd, websiteLd, itemListLd]}
      />

      {/* Hero */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-10 sm:py-14">
          <div className="mono-label mb-6">
            FS / 001 · Serum 2 preset packs · {site.location}
          </div>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-semibold tracking-tight leading-[0.95]">
            <ThreeDHeadline lines={['Sound design that', 'cuts through the mix.']} />
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-paper/70">
            Production-ready preset packs for Serum 2 — basses, plucks, pads and
            more. Instant download, yours forever.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              href="/packs"
              className="inline-grid place-items-center h-12 px-7 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition"
            >
              Browse catalog →
            </Link>
            {freePack && (
              <Link
                href={freePack.url}
                className="inline-grid place-items-center h-12 px-7 border border-line text-sm font-semibold hover:border-line-2 transition"
              >
                Start with a free pack
              </Link>
            )}
          </div>
          <div className="mt-7 mono-label flex flex-wrap gap-x-6 gap-y-1.5 text-muted">
            <span>{packs.length} releases</span>
            <span>Royalty-free</span>
            <span>Instant download</span>
            <span>Lifetime access</span>
            {promo.active && <span className="!text-emerald-400">15% off your first order</span>}
          </div>
        </div>
      </section>

      {/* Catalog grid */}
      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-12">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="mono-label">Releases</h2>
          <Link href="/packs" className="mono-label hover:text-paper transition-colors">All →</Link>
        </div>
        {packs.length === 0 ? (
          <p className="text-muted py-20 text-center">New packs dropping soon.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {packs.map((pack) => (
              <PackCard key={pack.id} pack={pack} />
            ))}
          </div>
        )}
      </section>
    </>
  )
}
